import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useClub } from '../club/ClubProvider.tsx'
import { dayText } from '../data/catalogueUi.ts'
import {
  browseOnly, EPHY_FILTERS, EPHY_PAGE, ephyCounts, ephyFlags, ephyPage, filterEphy, golfClass, isAuthorisedUsage, needsQuery, usageCount, useDebounced, useEphySearch, usageFacts, usageText,
  type EphyFilter, type EphyProduct, type EphyUsage,
} from '../data/ephy.ts'
import { fmtNum } from '../lib/format.ts'
import { BackLink, Banner, Field, Pill } from '../ui/components.tsx'
import { Picto } from '../ui/pictos.tsx'

type T = (k: string, o?: Record<string, unknown>) => string

/** Catalogue › "Produits autorisés (E-Phy)" (M21, docs/EPHY.md): the official French list of plant protection products (Anses, E-Phy open data, Licence Ouverte) as a
 *  READ-ONLY reference, with the legal frame "(à vérifier)". Online only (api.ephy_search, catalog.read), nothing kept on the device. Not linked to the club's stock,
 *  treatments or catalogue products (M15 will be); no recommendation, no dose computation: the doses and conditions are the file's, as given.
 *  M23 (docs/M23.md items 9 and 42): offline the page says so (the radio's word, or a call that never reached the server) instead of blaming the list; a product is one
 *  closed row (name, AMM, its class, how many turf uses) that opens on tap, the list shows 20 rows then "Afficher plus", and with no query a turf list waits behind its
 *  count and a hint (the whole list one tap away); on paper every shown row prints open. */
export function EphyPage() {
  const { t, i18n } = useTranslation()
  const { org, can } = useClub()
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<EphyFilter>('golf')
  const q = useDebounced(query)
  const search = useEphySearch(org.org_id, q, filter)
  const lang = i18n.language
  const data = search.fresh ? search.data : null
  const waiting = needsQuery(query, filter)
  const counts = data && filter !== 'all' ? ephyCounts(data.products) : null
  const rows = data ? filterEphy(data.products, filter) : []
  // the device has no network: the engine's or the browser's word, or a call that never reached the server (useRpc, item 9)
  const offline = !search.online || search.error === 'OFFLINE'
  const error = search.error && search.error !== 'OFFLINE' ? search.error : null
  // how much of the list is on screen, per query and filter (a new search starts again at one page, closed)
  const listKey = `${filter}|${q.trim()}`
  const [paging, setPaging] = useState({ key: listKey, shown: EPHY_PAGE, browse: false })
  const shown = paging.key === listKey ? paging.shown : EPHY_PAGE
  const browse = paging.key === listKey && paging.browse
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set())
  const toggle = (amm: string) => setOpen((s) => { const n = new Set(s); if (n.has(amm)) n.delete(amm); else n.add(amm); return n })
  const listed = browseOnly(q) && !browse ? null : ephyPage(rows, shown)

  return (
    <section className="flex flex-col gap-4" data-testid="ephy-page">
      <BackLink to="/catalogue" className="print:hidden">← {t('catalogue.title')}</BackLink>
      <h1 className="flex items-center gap-2 text-2xl font-bold"><Picto name="leaf" className="size-7 text-brand dark:text-fairway print:hidden" />{t('ephy.title')}</h1>
      <p className="meta">{t('ephy.help')}</p>
      <Banner tone="warn"><span data-testid="ephy-legal">{t('ephy.legal')}</span></Banner>
      {!can('catalog.read') && !error && <Banner tone="warn"><span data-testid="ephy-denied">{t('ephy.denied')}</span></Banner>}
      {offline && <Banner tone="warn"><span data-testid="ephy-offline">{t('ephy.offline')}</span></Banner>}
      {error && <Banner tone="error"><span data-testid="ephy-error">{error === 'PERMISSION_DENIED' || error === 'SCOPE_DENIED' ? t('ephy.denied') : t('ephy.failed')}</span></Banner>}
      {search.data && (search.data.source
        ? <p className="text-sm font-medium" data-testid="ephy-source">{t('ephy.source', { day: dayText(search.data.source.published_on) })}</p>
        : <Banner tone="info"><span data-testid="ephy-none-loaded">{t('ephy.noneLoaded')}</span></Banner>)}

      <div className="print:hidden">
        <Field label={t('ephy.search')} htmlFor="ephy-search" hint={t('ephy.searchHint')}>
          <input id="ephy-search" type="search" className="input" autoComplete="off" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('ephy.searchPlaceholder')} data-testid="ephy-search" />
        </Field>
      </div>
      {/* on paper: which list and which search this is (the controls are not printed) */}
      <p className="hidden text-sm font-semibold print:block" data-testid="ephy-printed">
        {t('ephy.printedList', { list: t(`ephy.filter.${filter}`) })}{q.trim() && <><br />{t('ephy.printedQuery', { query: q.trim() })}</>}
      </p>
      <div className="flex flex-col gap-2 print:hidden" role="group" aria-label={t('ephy.filterLabel')}>
        {EPHY_FILTERS.map((f) => (
          <button key={f} type="button" aria-pressed={filter === f} onClick={() => setFilter(f)} data-testid={`ephy-filter-${f}`}
            className={`btn !min-h-14 !justify-start text-left text-sm leading-snug ${filter === f ? 'btn-primary' : ''}`}>
            {counts && f !== 'all' ? t(`ephy.filter.${f}Count`, { count: counts[f] }) : t(`ephy.filter.${f}`)}
          </button>
        ))}
      </div>

      {waiting ? <p className="meta" data-testid="ephy-type-more">{t('ephy.typeMore')}</p>
        : !data ? (!offline && !error && <p className="meta">{t('common.loading')}</p>)
        : data.source && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="meta" data-testid="ephy-count">
                {data.total > data.products.length ? t('ephy.countShown', { count: rows.length, total: data.total }) : t('ephy.count', { count: rows.length })}
              </p>
              {listed && listed.rows.length > 0 && <button type="button" className="btn print:hidden" onClick={() => window.print()} data-testid="ephy-print">{t('ephy.print')}</button>}
            </div>
            {rows.length === 0 && <p className="meta" data-testid="ephy-empty">{t('ephy.empty')}</p>}
            {!listed ? rows.length > 0 && (
              <div className="flex flex-col gap-2" data-testid="ephy-browse">
                <p className="meta">{t('ephy.browseHint')}</p>
                <button type="button" className="btn" onClick={() => setPaging({ key: listKey, shown: EPHY_PAGE, browse: true })} data-testid="ephy-show-all">{t('ephy.showAll', { count: rows.length })}</button>
              </div>
            ) : (
              <>
                <ul className="flex flex-col gap-3" data-testid="ephy-list">
                  {listed.rows.map((p) => <ProductRow key={p.amm} p={p} open={open.has(p.amm)} onToggle={() => toggle(p.amm)} t={t} lang={lang} planLink={can('treatment.manage')} />)}
                </ul>
                {listed.hidden > 0 && (
                  <button type="button" className="btn print:hidden" onClick={() => setPaging({ key: listKey, shown: shown + EPHY_PAGE, browse: true })} data-testid="ephy-more">{t('ephy.showMore', { count: listed.hidden })}</button>
                )}
              </>
            )}
          </>
        )}
    </section>
  )
}

function ClassPill({ p, t }: { p: EphyProduct; t: T }) {
  const c = golfClass(p)
  if (c === 'golf') return <Pill tone="green">{t('ephy.class.golf')}</Pill>
  if (c === 'turf_banned') return <Pill tone="amber">{t('ephy.class.turf_banned')}</Pill>
  if (p.state !== 'AUTORISE') return <Pill tone="gray">{p.withdrawn_on ? t('ephy.class.withdrawnOn', { day: dayText(p.withdrawn_on) }) : t('ephy.class.withdrawn')}</Pill>
  return <Pill tone="gray">{t('ephy.class.noTurf')}</Pill>
}

/** One product: closed, a 56-px row (name, AMM, how many turf uses, its class and flags); open, the facts and every turf usage. On paper the row is open. */
function ProductRow({ p, open, onToggle, t, lang, planLink }: { p: EphyProduct; open: boolean; onToggle: () => void; t: T; lang: string; planLink: boolean }) {
  return (
    <li className="card flex flex-col break-inside-avoid !p-0 print:shadow-none" data-testid="ephy-row" data-amm={p.amm} data-open={open ? '1' : '0'}>
      <button type="button" className="flex min-h-14 w-full items-start justify-between gap-3 px-4 py-3 text-left print:pointer-events-none" aria-expanded={open} onClick={onToggle} data-testid="ephy-toggle">
        <span className="flex min-w-0 flex-col gap-1">
          <span className="text-lg font-semibold leading-snug" data-testid="ephy-name">{p.name}</span>
          <span className="meta"><span data-testid="ephy-amm">{t('ephy.amm', { amm: p.amm })}</span> · <span data-testid="ephy-usages-count">{t('ephy.usagesCount', { count: usageCount(p) })}</span></span>
          <span className="flex flex-wrap gap-1.5" data-testid="ephy-pills">
            <ClassPill p={p} t={t} />
            {ephyFlags(p).map((f) => <Pill key={f} tone="green">{t(`ephy.flag.${f}`)}</Pill>)}
          </span>
        </span>
        <Picto name="chevron" className={`mt-1 size-5 shrink-0 transition-transform print:hidden ${open ? '-rotate-90' : 'rotate-90'}`} strokeWidth={2.2} />
      </button>
      <div className={`${open ? 'flex' : 'hidden print:flex'} flex-col gap-2 px-4 pb-4`} data-testid="ephy-body">
        <dl className="grid grid-cols-1 gap-1 text-sm">
          {p.holder && <div><dt className="inline font-semibold">{t('ephy.holder')} </dt><dd className="inline">{p.holder}</dd></div>}
          <div><dt className="inline font-semibold">{t('ephy.type')} </dt><dd className="inline">{[p.product_type, p.functions].filter(Boolean).join(' · ')}</dd></div>
          {p.substances && <div><dt className="inline font-semibold">{t('ephy.substances')} </dt><dd className="inline break-words">{p.substances}</dd></div>}
          {p.other_names && <div><dt className="inline font-semibold">{t('ephy.otherNames')} </dt><dd className="inline break-words">{p.other_names}</dd></div>}
          {p.mentions.length > 0 && <div><dt className="inline font-semibold">{t('ephy.mentions')} </dt><dd className="inline" data-testid="ephy-mentions">{p.mentions.join(' · ')}</dd></div>}
        </dl>
        {/* M15-C: the one link to a treatment plan (the plan lives on a job: Planning › the job › Traitement; the catalogue product carries the AMM) */}
        {planLink && p.turf_authorised && <Link to="/plan" className="btn print:hidden" data-testid="ephy-use-in-plan"><span>{t('ephy.useInPlan')}</span></Link>}
        {planLink && p.turf_authorised && <p className="meta print:hidden">{t('ephy.useInPlanHint', { amm: p.amm })}</p>}
        <h3 className="text-sm font-bold">{t('ephy.turfUsages')}</h3>
        {p.turf_usages.length === 0 ? <p className="meta" data-testid="ephy-no-usage">{t('ephy.noUsage')}</p> : (
          <ul className="flex flex-col gap-2" data-testid="ephy-usages">
            {p.turf_usages.map((u, i) => <UsageRow key={i} u={u} t={t} lang={lang} />)}
          </ul>
        )}
      </div>
    </li>
  )
}

function UsageRow({ u, t, lang }: { u: EphyUsage; t: T; lang: string }) {
  const facts = usageFacts(u, t, (x) => fmtNum(x, lang, 6))
  return (
    <li className="rounded-xl border border-slate-200 p-3 text-sm dark:border-night-line" data-testid="ephy-usage">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold" data-testid="ephy-usage-label">{usageText(u.label)}</span>
        <Pill tone={isAuthorisedUsage(u) ? 'green' : 'gray'}>{u.state}</Pill>
      </div>
      {facts.length > 0 && <p data-testid="ephy-usage-facts">{facts.join(' · ')}</p>}
      {u.conditions && <p className="meta">{t('ephy.usage.conditions', { text: u.conditions })}</p>}
      {!isAuthorisedUsage(u) && u.end_of_use && <p className="meta">{t('ephy.usage.endOfUse', { day: dayText(u.end_of_use) })}</p>}
      {u.decided_on && <p className="meta">{t('ephy.usage.decided', { day: dayText(u.decided_on) })}</p>}
    </li>
  )
}
